import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Grid } from '@mui/material'
import connect from '../connect'
import SpacingContainerBase from '../../../components/layouts/SpacingContainerBase'
import Typography from '../../../components/common/Typography'
import { useTrans } from '../../../services/i18n'
import DetailsAnchorsHeader from '../../../components/generics/DetailsAnchorsHeader'
import SectionScrollable from '../../../components/common/Containers/SectionScrollable'
import { TextField } from '../../../components/common/Inputs'
import { Button } from '../../../components/common/Buttons'
import { Divider } from '../../../components/common/Divider'
import { V_ADMIN_ROLES } from '../../../utils/helpers'
import { SpacingContainer, Paper } from '../../../components/common/Containers'
import { RolePermissionsMatrix } from '../../../components/generics/RolePermissionsEditor'
import { useAlertContext } from '../../../contexts/AlertContext'
import { IconFilter } from '../../../components/svg/Icons'

const OWNER_ROLE_KEY = 'Owner'
const OWNER_SAFE_CAPABILITY_KEYS = ['organization-profile', 'organization-users']

const OrganizationRoleEditorDetail = ({
  currentRole,
  rolesList,
  capabilities,
  updateRole,
  updatePermissions,
  getRolesList,
}) => {
  const navigate = useNavigate()
  const { trans } = useTrans()
  const { successAlert, errorAlert } = useAlertContext()
  const [isSavingName, setIsSavingName] = useState(false)
  const [isSavingPermissions, setIsSavingPermissions] = useState(false)
  const { roleKeyEdit } = useParams()
  const [permissions, setPermissions] = useState({})
  const [name, setName] = useState('')
  const [key, setKey] = useState('')

  useEffect(() => {
    getRolesList()
  }, [])

  useEffect(() => {
    const roleFromStore =
      rolesList.find((role) => role.key === roleKeyEdit) ||
      (currentRole?.key === roleKeyEdit ? currentRole : null)

    if (roleFromStore) {
      setPermissions(roleFromStore.permissions || {})
      setName(roleFromStore.name || '')
      setKey(roleFromStore.key || '')
    }
  }, [currentRole, rolesList, roleKeyEdit])

  const allowedPermissionsByKey = useMemo(() => {
    if (key !== OWNER_ROLE_KEY) return {}

    return OWNER_SAFE_CAPABILITY_KEYS.reduce((acc, capabilityKey) => {
      acc[capabilityKey] = {
        view: true,
        edit: true,
        create: true,
        delete: true,
        viewLocked: true,
      }
      return acc
    }, {})
  }, [key])

  const groupedCapabilities = useMemo(() => {
    return capabilities.reduce((acc, capability) => {
      const category = capability.category || 'Organization'
      if (!acc[category]) acc[category] = []
      acc[category].push(capability)
      return acc
    }, {})
  }, [capabilities])

  const handlePermissionChange = (capabilityKey, type, checked) => {
    if (
      key === OWNER_ROLE_KEY &&
      OWNER_SAFE_CAPABILITY_KEYS.includes(capabilityKey) &&
      type === 'view' &&
      !checked
    ) {
      errorAlert('Owner must retain view access to organization profile and users.')
      return
    }

    setPermissions((prev) => ({
      ...prev,
      [capabilityKey]: {
        ...(prev[capabilityKey] || {}),
        [type]: checked,
      },
    }))
  }

  const savePermissions = async () => {
    try {
      setIsSavingPermissions(true)
      await updatePermissions({
        roleKey: key,
        permissions,
      })
      await getRolesList()
      successAlert('Role permissions updated')
    } catch (error) {
      errorAlert(error?.message || 'Server error, please refresh the page or try again later.')
    } finally {
      setIsSavingPermissions(false)
    }
  }

  const saveRole = async () => {
    try {
      setIsSavingName(true)
      await updateRole({ roleKey: key, name })
      await getRolesList()
      successAlert('Role name updated')
    } catch (error) {
      errorAlert('Server error, please refresh the page or try again later.')
    } finally {
      setIsSavingName(false)
    }
  }

  return (
    <SectionScrollable
      TopFixed={
        <DetailsAnchorsHeader
          anchors={V_ADMIN_ROLES}
          backLink="/role-editor"
          onPrint={undefined}
        />
      }
    >
      <SpacingContainerBase>
        <div id="role-info">
          <SpacingContainer py={0} px={7}>
            <Paper component={SpacingContainer} py={3} px="5sm3">
              <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                <Grid item xs zeroMinWidth sm={12}>
                  <Typography
                    style={{
                      alignItems: 'center',
                      display: 'flex',
                      fontSize: '20px',
                    }}
                    weight="500"
                    fontFamily="lexend"
                  >
                    {trans('role-information')}
                  </Typography>
                </Grid>
              </Grid>
              <Box style={{ alignItems: 'center', paddingTop: '20px' }}>
                <TextField
                  placeholder="Required"
                  type="text"
                  value={name}
                  name="name"
                  onChange={(ev) => setName(ev.target.value)}
                  required
                  label="Role Name"
                  style={{ width: '100%', marginRight: '16px' }}
                />
              </Box>
              <Box style={{ marginTop: '1rem' }}>
                <Grid container spacing={3}>
                  <Grid item xs zeroMinWidth>
                    <Button
                      variant="contained"
                      style={{ background: '#17b471', color: '#fff' }}
                      onClick={name ? saveRole : undefined}
                      pending={isSavingName}
                    >
                      Save Changes
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </SpacingContainer>
        </div>
        <div id="role-permissions">
          <SpacingContainer py={3} px={7}>
            <Paper component={SpacingContainer} py={3} px="5sm3">
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                style={{ marginBottom: '12px' }}
              >
                <Grid item xs zeroMinWidth>
                  <Typography
                    weight="500"
                    fontFamily="lexend"
                    style={{
                      alignItems: 'center',
                      display: 'flex',
                      fontSize: '20px',
                      marginTop: '12px',
                    }}
                  >
                    <IconFilter style={{ marginRight: '5px' }} />
                    {trans('role-permissions')}
                  </Typography>
                </Grid>
              </Grid>
              {Object.entries(groupedCapabilities).map(([category, items]) => (
                <div key={category}>
                  <Typography
                    weight="500"
                    fontFamily="lexend"
                    style={{
                      alignItems: 'center',
                      display: 'flex',
                      fontSize: '16px',
                      marginTop: '19px',
                    }}
                  >
                    {category}
                  </Typography>
                  <Divider light style={{ marginTop: '12px', marginBottom: '8px' }} />
                  <RolePermissionsMatrix
                    items={items}
                    permissions={permissions}
                    allowedPermissionsByKey={allowedPermissionsByKey}
                    onPermissionChange={handlePermissionChange}
                  />
                </div>
              ))}
              <Divider light style={{ marginTop: '12px', marginBottom: '8px' }} />
              <Box style={{ marginTop: '1rem' }}>
                <Grid container spacing={3}>
                  <Grid item xs zeroMinWidth>
                    <Button
                      variant="contained"
                      style={{ background: '#17b471', color: '#fff' }}
                      onClick={savePermissions}
                      pending={isSavingPermissions}
                    >
                      Save Changes
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </SpacingContainer>
        </div>
      </SpacingContainerBase>
    </SectionScrollable>
  )
}

export default connect(OrganizationRoleEditorDetail)
